import React, {
  ReactElement,
  useEffect,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { InstantSearch } from 'react-instantsearch-dom';
import { isEqual } from 'lodash';

import { BrowserRouter, Route } from 'react-router-dom';
import { ALGOLIA_ALL_LISTINGS_INDEX } from '../../../utils/constants';
import { searchStateToUrl, urlToSearchState } from '../../../utils/formatters';
import SearchResultsBlock from './search-results-block';
import useURLSync from '../../../hooks/useURLSync';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import { LocationParams, SearchState } from '../../search/admin-dashboard-tables/interfaces';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';

interface SortParams extends LocationParams {
  sortBy?: string,
}

const Search = (props) => {
  const {
    children,
    shouldRedirect,
    syncURL,
    newArrivals,
  } = props;

  const getInitialSort = (isNewArrivals: boolean) => {
    const {
      lat,
      lng,
      sortBy,
    }: SortParams = fetchSearchParams();
    if (lat && lng) {
      return 'location';
    }
    if (sortBy) {
      return sortBy;
    }
    if (isNewArrivals) {
      return `${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`;
    }
    return ALGOLIA_ALL_LISTINGS_INDEX!;
  };

  const getInitialIndexName = (currentSort: string) => {
    const { sortBy }: SortParams = fetchSearchParams();
    if (sortBy) {
      return sortBy;
    }
    if (currentSort) {
      if (currentSort === 'location') {
        return ALGOLIA_ALL_LISTINGS_INDEX!;
      }
      return currentSort;
    }
    return ALGOLIA_ALL_LISTINGS_INDEX!;
  };

  const {
    user,
    searchState: propsSearchState,
    onSearchStateChange,
    createURL,
  } = useURLSync(syncURL);

  const { searchClient } = useAlgoliaSearchClientLite(user?.public_key);

  const [searchState, setSearchState] = useState(
    propsSearchState || urlToSearchState(window.location),
  );
  // This is the value used to track the form state (not the indexName passed to Algolia)
  const [currentSort, setCurrentSort] = useState(getInitialSort(newArrivals));
  // This is the actual indexName passed to Algolia
  const [indexName, setIndexName] = useState(getInitialIndexName(currentSort));

  useEffect(() => {
    const newSearchState: SearchState = { ...propsSearchState };

    if (currentSort) {
      if (currentSort === 'location') {
        newSearchState.sortBy = ALGOLIA_ALL_LISTINGS_INDEX!;
      } else {
        newSearchState.sortBy = currentSort;
      }
      setIndexName(newSearchState.sortBy);
    }

    if (newSearchState?.page && newSearchState?.configure?.page) {
      if (newSearchState.page !== newSearchState.configure.page) {
        newSearchState.page = newSearchState.configure.page;
      }
    }
    if (!isEqual(newSearchState, searchState)) {
      setSearchState(newSearchState);
    }
  }, [propsSearchState, currentSort]);

  function customSearchStateChange(newSearchState, redirect) {
    if (!isEqual(newSearchState, props.searchState)) {
      onSearchStateChange(newSearchState);
      setSearchState(newSearchState);
    }
    if (redirect) {
      window.location.href = searchStateToUrl('/search', newSearchState);
    }
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      searchState={searchState}
      createURL={syncURL ? createURL : () => {}}
      onSearchStateChange={(updatedSearchState) => {
        if (syncURL) {
          customSearchStateChange(updatedSearchState, shouldRedirect);
        }
      }}
    >
      {/* eslint-disable-next-line */}
      {React.cloneElement(children, { searchState, setSearchState, currentSort, setCurrentSort })}
    </InstantSearch>
  );
};

const SearchComponent = Search;

const SearchContainer: React.FC<any> = (props:any): ReactElement => {
  const {
    breedSlug,
    sellerSlug,
    listingsType,
    categorySlug,
    showNonPublished = false,
    filterByState,
    onlyOnSale,
    availableToday,
    underFiveHundred,
    newArrivals,
  } = props;
  return (
    <SearchComponent {...props} shouldRedirect={false}>
      <SearchResultsBlock
        {...props}
        breedSlug={breedSlug}
        sellerSlug={sellerSlug}
        listingsType={listingsType}
        categorySlug={categorySlug}
        showNonPublished={showNonPublished}
        filterByState={filterByState}
        onlyOnSale={onlyOnSale}
        availableToday={availableToday}
        underFiveHundred={underFiveHundred}
        newArrivals={newArrivals}
      />
    </SearchComponent>
  );
};

export { SearchContainer, SearchComponent };

if (document.getElementsByClassName('react-search').length > 0) {
  document.querySelectorAll('.react-search')
    .forEach((element) => {
      const { user } = window;
      const seller = element.getAttribute('data-seller') ?? '';
      const listingsType = element.getAttribute('data-listingstype') || 'puppy';
      const showNonPublished = element.getAttribute('data-shownonpublished') ?? false;
      const category = element.getAttribute('data-category') ?? null;
      const filterByState = element.getAttribute('data-state') ?? '';
      const onlyOnSale = element.getAttribute('data-puppies-on-sale') ?? '';
      const availableToday = element.getAttribute('data-puppies-available-today') ?? '';
      const underFiveHundred = element.getAttribute('data-under-five-hundred') ?? '';
      const newArrivals = element.getAttribute('data-new-arrivals') ?? '';
      ReactDOM.render(
        <BrowserRouter>
          <Route
            path="/"
            render={(routeProps) => (
              <SearchContainer
                sellerSlug={seller}
                categorySlug={category}
                user={user}
                listingsType={listingsType}
                showNonPublished={!!showNonPublished}
                syncURL={true}
                filterByState={filterByState}
                onlyOnSale={onlyOnSale}
                availableToday={availableToday}
                underFiveHundred={underFiveHundred}
                newArrivals={newArrivals}
                {...routeProps}
              />
            )}
          />
        </BrowserRouter>,
        element,
      );
    });
}
